/**
 * Translation management.
 *
 * WHO THIS SCREEN IS FOR
 * A translator, not a developer. Everything here exists so that fixing a wrong
 * word is a spreadsheet edit and an upload, rather than a ticket, a developer,
 * a build and a deploy. That is the whole feature; the rest is detail.
 *
 * THE UPLOAD IS TWO STEPS, ALWAYS
 * The file is sent once with `dryRun` to be judged and once more to be applied,
 * and the summary in between is not optional. An import that silently
 * half-applies — "812 saved, 4 skipped" with no way to learn which four — is
 * worse than one that refuses, because the four that vanished are the ones the
 * translator most needed to see. Nothing is written until the numbers have been
 * read.
 *
 * CAPABILITY
 * `org.update`, which is administrator-only. Hiding the screen is a usability
 * affordance; the server re-checks on every request, as it does everywhere.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LANGUAGE_NAMES, type Language } from '@opencomply/shared';

import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import { downloadFile } from '../lib/download';
import { refreshOverrides } from '../i18n';
import {
  Card, CardHeader, Button, Field, Modal, Skeleton, ErrorNotice, ProgressBar, Pill,
} from '../components/ui';
import { IconLanguage } from '../components/icons';

interface LocaleCoverage {
  locale: Language;
  name: string;
  shipsComplete: boolean;
  totalKeys: number;
  translatedKeys: number;
  missingKeys: number;
  percent: number;
  overrideCount: number;
  lastImport: { at: string; by: { id: string; name: string; email: string } | null } | null;
}

interface TranslationIssue {
  key: string;
  code: string;
  detail?: string;
}

interface ImportSummary {
  locale: Language;
  format: 'csv' | 'json';
  dryRun: boolean;
  rowsRead: number;
  changed: number;
  unchanged: number;
  blank: number;
  reverted: number;
  rejected: TranslationIssue[];
  preview: Array<{ key: string; english: string; from: string; to: string }>;
}

const QUERY_KEY = ['translations', 'coverage'] as const;

/** Tone for the coverage bar. Not decoration: 0% and 99% need to look different. */
function toneFor(percent: number): 'good' | 'warning' | 'serious' {
  if (percent >= 95) return 'good';
  if (percent >= 50) return 'warning';
  return 'serious';
}

export function TranslationsPage() {
  const { t, i18n } = useTranslation();
  const { allows } = useAuth();
  const describeError = useApiError();
  const queryClient = useQueryClient();

  const canManage = allows('org.update');

  const coverage = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.get<LocaleCoverage[]>('/translations'),
    enabled: canManage,
  });

  // --- Export ---------------------------------------------------------------
  const exporting = useRef<AbortController | null>(null);
  const [exportingKey, setExportingKey] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  async function exportLocale(locale: Language, format: 'csv' | 'json') {
    exporting.current?.abort();
    const controller = new AbortController();
    exporting.current = controller;
    setExportingKey(`${locale}:${format}`);
    setExportError(null);
    try {
      await downloadFile(
        `/translations/${locale}/export?format=${format}`,
        `opencomply-${locale}.${format}`,
        '/translations',
        controller.signal,
      );
    } catch (err) {
      if (!controller.signal.aborted) setExportError(describeError(err));
    } finally {
      setExportingKey(null);
    }
  }

  // --- Import ---------------------------------------------------------------
  const [uploadFor, setUploadFor] = useState<LocaleCoverage | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [pending, setPending] = useState<{ format: 'csv' | 'json'; content: string } | null>(null);
  const [applied, setApplied] = useState<number | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  function closeUpload() {
    setUploadFor(null);
    setSummary(null);
    setPending(null);
    setApplied(null);
    setImportError(null);
  }

  const submitImport = useMutation({
    mutationFn: (input: {
      locale: Language;
      format: 'csv' | 'json';
      content: string;
      dryRun: boolean;
    }) =>
      api.post<ImportSummary>(`/translations/${input.locale}/import`, {
        format: input.format,
        content: input.content,
        dryRun: input.dryRun,
      }),
  });

  /**
   * Read the chosen file in the browser and ask the server to judge it.
   *
   * The format is taken from the extension rather than guessed from the
   * content: a translator who renames a file has made a mistake worth telling
   * them about, and sniffing would hide it until the rows came back wrong.
   */
  async function onFileChosen(file: File, locale: Language) {
    setImportError(null);
    setSummary(null);
    setApplied(null);
    const format: 'csv' | 'json' = file.name.toLowerCase().endsWith('.json') ? 'json' : 'csv';
    const content = await file.text();
    setPending({ format, content });
    try {
      const result = await submitImport.mutateAsync({ locale, format, content, dryRun: true });
      setSummary(result);
    } catch (err) {
      setImportError(describeError(err));
    }
  }

  async function applyImport() {
    if (!uploadFor || !pending) return;
    setImportError(null);
    try {
      const result = await submitImport.mutateAsync({
        locale: uploadFor.locale,
        format: pending.format,
        content: pending.content,
        dryRun: false,
      });
      setApplied(result.changed + result.reverted);
      setSummary(null);
      setPending(null);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      // The strings the administrator is reading right now may be among the
      // ones that just changed. Re-fetching the override layer is what makes
      // this screen honest about having applied anything — without it the
      // confirmation would claim a change the page itself does not show.
      refreshOverrides();
    } catch (err) {
      setImportError(describeError(err));
    }
  }

  // --- Reset ----------------------------------------------------------------
  const resetLocale = useMutation({
    mutationFn: (locale: Language) => api.post(`/translations/${locale}/reset`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  if (!canManage) {
    return (
      <Card>
        <ErrorNotice message={t('error.INSUFFICIENT_PERMISSIONS')} />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader title={t('translations.title')} subtitle={t('translations.subtitle')} />
        <p className="px-4 sm:px-5 pb-4 text-sm text-ink-muted">{t('translations.exportHint')}</p>
      </Card>

      {exportError && <ErrorNotice message={exportError} />}

      {coverage.isLoading && (
        <Card>
          <div className="p-4 space-y-3">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
        </Card>
      )}

      {coverage.isError && (
        <ErrorNotice
          message={describeError(coverage.error)}
          onRetry={() => void coverage.refetch()}
        />
      )}

      {coverage.data?.map((row) => (
        <Card key={row.locale}>
          <div className="p-4 sm:p-5 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <IconLanguage className="w-5 h-5 text-ink-muted" />
              {/* The endonym, so a speaker of this language recognises it here
                  too — not only in the switcher. */}
              <h3 className="oc-h3">{LANGUAGE_NAMES[row.locale] ?? row.name}</h3>
              <span className="font-mono text-xs uppercase text-ink-muted">{row.locale}</span>
              <Pill tone={row.shipsComplete ? 'good' : 'neutral'}>
                {row.shipsComplete ? t('translations.complete') : t('translations.stub')}
              </Pill>
            </div>

            <ProgressBar
              value={row.percent}
              tone={toneFor(row.percent)}
              label={t('translations.coverage')}
            />

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-muted">
              <span>
                {t('translations.translatedOf', {
                  count: row.translatedKeys,
                  total: row.totalKeys,
                })}
              </span>
              {row.missingKeys > 0 && (
                <span>{t('translations.missing', { count: row.missingKeys })}</span>
              )}
              <span>{t('translations.overrides', { count: row.overrideCount })}</span>
              <span>
                {t('translations.lastImport')}:{' '}
                {row.lastImport
                  ? `${row.lastImport.by?.name ?? row.lastImport.by?.email ?? '—'} · ${new Date(
                      row.lastImport.at,
                    ).toLocaleString(i18n.language)}`
                  : t('translations.neverImported')}
              </span>
            </div>

            <p className="text-xs text-ink-muted">
              {row.shipsComplete ? t('translations.completeHint') : t('translations.stubHint')}
            </p>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                onClick={() => void exportLocale(row.locale, 'csv')}
                disabled={exportingKey === `${row.locale}:csv`}
              >
                {t('translations.exportCsv')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => void exportLocale(row.locale, 'json')}
                disabled={exportingKey === `${row.locale}:json`}
              >
                {t('translations.exportJson')}
              </Button>
              <Button onClick={() => { closeUpload(); setUploadFor(row); }}>
                {t('translations.import')}
              </Button>
              {row.overrideCount > 0 && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (!confirm(t('translations.resetConfirm', { name: LANGUAGE_NAMES[row.locale] }))) return;
                    resetLocale.mutate(row.locale);
                  }}
                >
                  {t('translations.reset')}
                </Button>
              )}
            </div>
          </div>
        </Card>
      ))}

      <Modal
        open={uploadFor !== null}
        onClose={closeUpload}
        title={t('translations.importTitle', {
          name: uploadFor ? LANGUAGE_NAMES[uploadFor.locale] : '',
        })}
        footer={
          <>
            <Button variant="ghost" onClick={closeUpload}>{t('common.cancel')}</Button>
            {summary && summary.changed + summary.reverted > 0 && (
              <Button onClick={() => void applyImport()} disabled={submitImport.isPending}>
                {t('translations.apply', { count: summary.changed + summary.reverted })}
              </Button>
            )}
          </>
        }
      >
        {applied === null && (
          <Field label={t('translations.importFile')} hint={t('translations.importFileHint')} htmlFor="tr-file">
            <input
              id="tr-file"
              type="file"
              accept=".csv,.json,text/csv,application/json"
              className="oc-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && uploadFor) void onFileChosen(file, uploadFor.locale);
              }}
            />
          </Field>
        )}

        {submitImport.isPending && <p className="text-sm text-ink-muted">{t('translations.checking')}…</p>}
        {importError && <ErrorNotice message={importError} />}

        {applied !== null && (
          <p className="text-sm text-good">{t('translations.applied', { count: applied })}</p>
        )}

        {summary && (
          <div className="space-y-4">
            <div>
              <h3 className="oc-h3">{t('translations.summaryTitle')}</h3>
              <p className="text-xs text-ink-muted">{t('translations.summaryHint')}</p>
            </div>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <dt className="text-ink-muted">{t('translations.rowsRead')}</dt>
              <dd className="text-right tabular-nums">{summary.rowsRead}</dd>
              <dt className="text-ink-muted">{t('translations.changed')}</dt>
              <dd className="text-right tabular-nums font-semibold">{summary.changed}</dd>
              <dt className="text-ink-muted">{t('translations.reverted')}</dt>
              <dd className="text-right tabular-nums">{summary.reverted}</dd>
              <dt className="text-ink-muted">{t('translations.unchanged')}</dt>
              <dd className="text-right tabular-nums">{summary.unchanged}</dd>
              <dt className="text-ink-muted">{t('translations.blank')}</dt>
              <dd className="text-right tabular-nums">{summary.blank}</dd>
              <dt className="text-ink-muted">{t('translations.rejected')}</dt>
              <dd
                className={
                  summary.rejected.length > 0
                    ? 'text-right tabular-nums font-semibold text-critical'
                    : 'text-right tabular-nums'
                }
              >
                {summary.rejected.length}
              </dd>
            </dl>

            {summary.changed + summary.reverted === 0 && summary.rejected.length === 0 && (
              <p className="text-sm text-ink-muted">{t('translations.nothingToApply')}</p>
            )}

            {summary.rejected.length > 0 && (
              <div>
                <h4 className="oc-label">{t('translations.rejectedTitle')}</h4>
                <p className="text-xs text-ink-muted mb-2">{t('translations.rejectedHint')}</p>
                <ul className="space-y-2">
                  {summary.rejected.slice(0, 50).map((issue) => (
                    <li key={`${issue.key}:${issue.code}`} className="text-xs">
                      <span className="font-mono text-ink-secondary">{issue.key}</span>
                      <span className="block text-critical">
                        {t(`translations.issue.${issue.code}`, { defaultValue: issue.code })}
                      </span>
                      {issue.detail && <span className="block text-ink-muted">{issue.detail}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {summary.preview.length > 0 && (
              <div>
                <h4 className="oc-label">{t('translations.previewTitle')}</h4>
                <ul className="space-y-2">
                  {summary.preview.map((row) => (
                    <li key={row.key} className="text-xs">
                      <span className="font-mono text-ink-secondary">{row.key}</span>
                      <span className="block text-ink-muted line-through">{row.from || '—'}</span>
                      <span className="block text-ink">{row.to}</span>
                    </li>
                  ))}
                </ul>
                {summary.changed + summary.reverted > summary.preview.length && (
                  <p className="mt-2 text-xs text-ink-muted">
                    {t('translations.previewTruncated', { count: summary.preview.length })}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
